import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { ApiQueryParams } from '@/api/Api';
import {
  createPageElements,
  type CreatePageElementsResponse,
} from '@/api/page_elements/create';
import {
  listPageElements,
  type GetPageElementsListRequest,
  type PageElementsListResponse,
} from '@/api/page_elements/list';
import {
  readPageElements,
  type ReadPageElementsResponse,
} from '@/api/page_elements/read';
import {
  updatePageElements,
  type UpdatePageElementsResponse,
} from '@/api/page_elements/update';
import {
  deletePageElements,
  type DeletePageElementsResponse,
} from '@/api/page_elements/delete';
import {
  clonePageElements,
  type ClonePageElementsResponse,
} from '@/api/page_elements/clone';
import {
  importPageElements,
  type ImportPageElementsResponse,
} from '@/api/page_elements/import';
import {
  exportPageElements,
  type GetPageElementsExportRequest,
} from '@/api/page_elements/export';
import {
  getPageElementsImportStatus,
  type PageElementsImportStatusResponse,
} from '@/api/page_elements/bulkImportStatus';
import type {
  CreatePageElementsPayload,
  UpdatePageElementsPayload,
  PageElementsResponse,
} from '@/interfaces/element.interface';
import { buildParams } from './utils';
import { notifyApiError, shouldSuppressErrorToast } from './error';

const PAGE_ELEMENTS_KEY = 'page-elements';
const PAGE_ELEMENTS_LIST_KEY = 'page-elements-list';
const PAGE_ELEMENTS_IMPORT_STATUS_KEY = 'page-elements-import-status';

type PageElementsQueryOptions = Omit<
  UseQueryOptions<
    ReadPageElementsResponse,
    unknown,
    ReadPageElementsResponse,
    [typeof PAGE_ELEMENTS_KEY, string]
  >,
  'queryKey' | 'queryFn'
>;

export const usePageElements = (
  websiteInterfaceId: string | undefined,
  options?: PageElementsQueryOptions
) => {
  const finalParams = buildParams('page_element', {}, 'read');

  const query = useQuery<ReadPageElementsResponse, unknown>({
    queryKey: [PAGE_ELEMENTS_KEY, websiteInterfaceId || ''],
    queryFn: async () => {
      if (!websiteInterfaceId) {
        throw new Error('websiteInterfaceId is required to fetch page elements');
      }
      const response = await readPageElements(websiteInterfaceId, finalParams);
      return response.data;
    },
    ...options,
    enabled: Boolean(websiteInterfaceId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'page_element', action: 'read' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

type PageElementsListQueryOptions = Omit<
  UseQueryOptions<
    PageElementsListResponse,
    unknown,
    PageElementsListResponse,
    [typeof PAGE_ELEMENTS_LIST_KEY, GetPageElementsListRequest]
  >,
  'queryKey' | 'queryFn'
>;

export const usePageElementsList = (
  params?: Partial<GetPageElementsListRequest>,
  options?: PageElementsListQueryOptions
) => {
  const finalParams: GetPageElementsListRequest = {
    page: params?.page ?? 1,
    page_limit: params?.page_limit ?? 50,
    ...buildParams('page_element', params ?? {}, 'list'),
    ...(params ?? {}),
  };

  const query = useQuery<PageElementsListResponse, unknown>({
    queryKey: [PAGE_ELEMENTS_LIST_KEY, finalParams],
    queryFn: async () => {
      const response = await listPageElements(finalParams);
      return response.data;
    },
    ...options,
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'page_element', action: 'list' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

type CreatePageElementsVariables = {
  data: CreatePageElementsPayload;
  params?: Partial<ApiQueryParams>;
};

type CreatePageElementsOptions = UseMutationOptions<
  CreatePageElementsResponse,
  unknown,
  CreatePageElementsVariables
>;

export const useCreatePageElements = (
  options?: CreatePageElementsOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CreatePageElementsResponse, unknown, CreatePageElementsVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('page_element', params, 'create');
      const response = await createPageElements(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'create' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      const interfaceId = variables.data.website_interface_id;
      if (interfaceId) {
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_KEY, interfaceId] });
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_LIST_KEY] });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type UpdatePageElementsVariables = {
  websiteInterfaceId: string;
  data: UpdatePageElementsPayload;
  params?: Partial<ApiQueryParams>;
};

type UpdatePageElementsOptions = UseMutationOptions<
  UpdatePageElementsResponse,
  unknown,
  UpdatePageElementsVariables
>;

export const useUpdatePageElements = (
  options?: UpdatePageElementsOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<UpdatePageElementsResponse, unknown, UpdatePageElementsVariables>({
    mutationFn: async ({ websiteInterfaceId, data, params }) => {
      const finalParams = buildParams('page_element', params, 'update');
      const response = await updatePageElements(websiteInterfaceId, finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'update' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      const interfaceId = variables.websiteInterfaceId;
      if (interfaceId) {
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_KEY, interfaceId] });
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_LIST_KEY] });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type DeletePageElementsVariables = {
  websiteInterfaceId: string;
  params?: Partial<ApiQueryParams>;
};

type DeletePageElementsOptions = UseMutationOptions<
  DeletePageElementsResponse,
  unknown,
  DeletePageElementsVariables
>;

export const useDeletePageElements = (
  options?: DeletePageElementsOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<DeletePageElementsResponse, unknown, DeletePageElementsVariables>({
    mutationFn: async ({ websiteInterfaceId, params }) => {
      const finalParams = buildParams('page_element', params, 'delete');
      const response = await deletePageElements(websiteInterfaceId, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'delete' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      const interfaceId = variables.websiteInterfaceId;
      if (interfaceId) {
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_KEY, interfaceId] });
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_LIST_KEY] });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type ClonePageElementsVariables = {
  sourceWebsiteInterfaceId: string;
  targetWebsiteInterfaceId: string;
  params?: Partial<ApiQueryParams>;
};

type ClonePageElementsOptions = UseMutationOptions<
  ClonePageElementsResponse,
  unknown,
  ClonePageElementsVariables
>;

export const useClonePageElements = (
  options?: ClonePageElementsOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ClonePageElementsResponse, unknown, ClonePageElementsVariables>({
    mutationFn: async ({ sourceWebsiteInterfaceId, targetWebsiteInterfaceId, params }) => {
      const finalParams = buildParams('page_element', params, 'clone');
      const response = await clonePageElements(sourceWebsiteInterfaceId, targetWebsiteInterfaceId, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'clone' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      const targetInterfaceId = variables.targetWebsiteInterfaceId;
      if (targetInterfaceId) {
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_KEY, targetInterfaceId] });
        queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_LIST_KEY] });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type ImportPageElementsVariables = {
  data: File | string | unknown;
  params?: Partial<ApiQueryParams>;
};

type ImportPageElementsOptions = UseMutationOptions<
  ImportPageElementsResponse,
  unknown,
  ImportPageElementsVariables
>;

export const useImportPageElements = (
  options?: ImportPageElementsOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ImportPageElementsResponse, unknown, ImportPageElementsVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('page_element', params, 'import');
      const response = await importPageElements(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'import' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [PAGE_ELEMENTS_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ExportPageElementsVariables = {
  params?: Partial<GetPageElementsExportRequest>;
  filename?: string;
  autoDownload?: boolean;
};

type ExportPageElementsOptions = UseMutationOptions<
  unknown,
  unknown,
  ExportPageElementsVariables
>;

export const useExportPageElements = (
  options?: ExportPageElementsOptions
) => {
  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<unknown, unknown, ExportPageElementsVariables>({
    mutationFn: async ({ params, filename, autoDownload = true }) => {
      const { filters, ...paramsWithoutFilters } = params ?? {};
      const baseParams = buildParams('page_element', paramsWithoutFilters, 'export');
      const finalParams = {
        ...baseParams,
        ...(filters ? { filters } : {}),
        responseType: 'blob' as const,
      };

      const response = await exportPageElements(finalParams);

      if (response.data instanceof Blob && autoDownload) {
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `page-elements-export-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'page_element', action: 'export' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
};

type PageElementsImportStatusQueryOptions = Omit<
  UseQueryOptions<
    PageElementsImportStatusResponse,
    unknown,
    PageElementsImportStatusResponse,
    [typeof PAGE_ELEMENTS_IMPORT_STATUS_KEY, string]
  >,
  'queryKey' | 'queryFn'
>;

export const usePageElementsImportStatus = (
  bulkImportId: string | undefined,
  options?: PageElementsImportStatusQueryOptions
) => {
  const finalParams = buildParams('page_element', {}, 'read');

  const query = useQuery<PageElementsImportStatusResponse, unknown>({
    queryKey: [PAGE_ELEMENTS_IMPORT_STATUS_KEY, bulkImportId || ''],
    queryFn: async () => {
      if (!bulkImportId) {
        throw new Error('bulkImportId is required');
      }
      const response = await getPageElementsImportStatus(bulkImportId, finalParams);
      return response.data;
    },
    ...options,
    enabled: Boolean(bulkImportId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      if (import.meta.env.DEV) {
        console.warn('[query] Page elements import status check failed', query.error);
      }
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};